import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  label?: string;
  center?: boolean;
  delay?: number;
  fullScreen?: boolean;
}

const sizes = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
} as const;

const variants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
} as const;

export function LoadingSpinner({ 
  size = 'md', 
  className = '', 
  label = 'Loading...', 
  center = false,
  delay = 0,
  fullScreen = false,
}: LoadingSpinnerProps) {
  const [show, setShow] = React.useState(!delay);

  React.useEffect(() => {
    if (!delay) return;

    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  if (!show) return null;

  const spinner = (
    <AnimatePresence>
      <motion.div
        role="status"
        className={cn(
          'flex items-center justify-center gap-2',
          center && 'min-h-[200px]',
          className
        )}
        aria-label={label}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.2 }}
      >
        <Loader2 
          className={cn(
            'animate-spin text-primary-400',
            sizes[size]
          )}
          aria-hidden="true"
        />
        <span className="sr-only">{label}</span>
      </motion.div>
    </AnimatePresence>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-dark-bg/50 backdrop-blur-sm flex items-center justify-center z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
}